import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { showConfirm } from './ConfirmDialog';
import Programs from './Programs';
import Exercises from './Exercises';
import ClientProfile from './ClientProfile';
import Bookings from './Bookings';
import BrandAdmin from './BrandAdmin';
import HomeView from './HomeView';
import ClientsView from './ClientsView';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { STATUS_COLORS, NEUTRAL, resolveTheme } from './designTokens';

const SIDEBAR_WIDTH = 220;

function Dashboard({ session, isPlatformAdmin, onNavigateToOwner }) {
  const [therapist, setTherapist] = useState(null);
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState('hem');
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientUnreadMsg, setClientUnreadMsg] = useState({});
  const [newClientBookCount, setNewClientBookCount] = useState(0);
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [accentColor, setAccentColor] = useState(KLINIO_DEFAULT_ACCENT);
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  const [brandDisplayName, setBrandDisplayName] = useState(null);
  const [bookFor, setBookFor] = useState(null); // kund som ska bokas (från kundkortets "+ Ny bokning", T-16 T6)
  const [brandTagline, setBrandTagline] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Uppdatera Hem-vyn varje gång terapeuten navigerar dit,
  // så att bokningar som markerats som sedda i Bokningar-fliken försvinner.
  // Nollställ badge direkt när terapeuten öppnar Bokningar (optimistiskt).
  useEffect(() => {
    if (activeTab === 'hem' && therapist) {
      fetchNotifCounts(therapist);
    }
    if (activeTab === 'bookings') {
      setNewClientBookCount(0);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'hem' || !therapist) return;
    const interval = setInterval(() => fetchNotifCounts(therapist), 30000);
    return () => clearInterval(interval);
  }, [activeTab, therapist, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);

    const { data: therapistData } = await supabase
      .from('therapists')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    setTherapist(therapistData);

    let clientsData = [];
    if (therapistData) {
      const [{ data }, { data: brandData }] = await Promise.all([
        supabase.from('clients').select('*').eq('therapist_id', therapistData.id).order('created_at', { ascending: false }),
        supabase.from('brand_settings').select('accent_color, logo_url, display_name, tagline').eq('therapist_id', therapistData.id).maybeSingle(),
      ]);
      clientsData = data || [];
      setClients(clientsData);
      if (brandData?.accent_color) setAccentColor(brandData.accent_color);
      if (brandData?.logo_url) setBrandLogoUrl(brandData.logo_url);
      if (brandData?.display_name) setBrandDisplayName(brandData.display_name);
      if (brandData?.tagline) setBrandTagline(brandData.tagline);
    }

    setLoading(false);
    if (therapistData) fetchNotifCounts(therapistData);
  };

  const fetchNotifCounts = async (therapistData) => {
    // Olästa meddelanden från kunder, grupperat per kund
    const { data: unreadMsgs } = await supabase
      .from('messages')
      .select('client_id')
      .eq('therapist_id', therapistData.id)
      .eq('sender_role', 'client')
      .eq('read', false);

    const msgMap = {};
    (unreadMsgs || []).forEach(m => {
      msgMap[m.client_id] = (msgMap[m.client_id] || 0) + 1;
    });
    setClientUnreadMsg(msgMap);

    // Nya klientbokningar terapeuten inte sett (badge i sidomenyn)
    const { data: unseenData } = await supabase
      .from('bookings')
      .select('id')
      .eq('therapist_id', therapistData.id)
      .eq('therapist_seen', false)
      .neq('status', 'cancelled')
      .order('date', { ascending: true })
      .order('start_time', { ascending: true });
    setNewClientBookCount((unseenData || []).length);
  };

  const isNewClient = (client) => {
    if (client.new_seen) return false;
    if (!client.created_at) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(client.created_at) > sevenDaysAgo;
  };

  const markClientAsSeen = async (clientId) => {
    await supabase.from('clients').update({ new_seen: true }).eq('id', clientId);
    setClients(prev => prev.map(c => c.id === clientId ? { ...c, new_seen: true } : c));
  };

  // DPIA A8: varna om e-postadressen redan finns hos en av klinikens kunder.
  const newEmailNorm = newClientEmail.trim().toLowerCase();
  const duplicateEmailClient = newEmailNorm.includes('@')
    ? clients.find(c => (c.email || '').trim().toLowerCase() === newEmailNorm)
    : null;

  const submitNewClient = async (e) => {
    e.preventDefault();
    if (!newClientName.trim()) return;
    setAddingClient(true);
    const { error } = await supabase.from('clients').insert({
      therapist_id: therapist.id,
      name: newClientName.trim(),
      email: newClientEmail.trim().toLowerCase() || null,
      phone: newClientPhone.trim() || null,
    });
    setAddingClient(false);
    if (error) { showToast('Kunde inte lägga till kund.', 'error'); return; }
    setShowAddClientForm(false);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientPhone('');
    fetchData();
  };

  const cancelAddClient = () => {
    setShowAddClientForm(false);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientPhone('');
  };

  const deleteClient = (id) => {
    showConfirm({
      title: 'Ta bort kund',
      description: 'Kunden tas bort. Kopplad historik som bokningar, meddelanden och programdata kan också påverkas beroende på databasens relationer. Åtgärden kan inte ångras.',
      onConfirm: async () => {
        const { error } = await supabase.from('clients').delete().eq('id', id);
        if (error) {
          console.error('deleteClient error:', error);
          showToast('Kunden kunde inte raderas. Försök igen eller kontakta support om problemet kvarstår.');
          return;
        }
        setSelectedClient(null);
        fetchData();
      },
    });
  };

  if (loading) return <p>Laddar...</p>;

  const theme = resolveTheme(accentColor);

  // ─── Nav-ikoner (inline SVG, stroke-baserade) ─────────────────────────────────
  const IcoHem      = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 7L8 2l6 5v7h-4v-4H6v4H2V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>;
  const IcoKunder   = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M2 14c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
  const IcoOvningar = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M1 8h2l2-4 3 8 2-5 1.5 3H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  const IcoProgram  = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>;
  const IcoBokningar= () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 2v2M11 2v2M2 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
  const IcoBrand    = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;

  const mainNavItems = [
    { key: 'hem',       label: 'Hem',       icon: <IcoHem /> },
    { key: 'clients',   label: 'Kunder',    icon: <IcoKunder /> },
    { key: 'exercises', label: 'Övningar',  icon: <IcoOvningar /> },
    { key: 'programs',  label: 'Program',   icon: <IcoProgram /> },
    { key: 'bookings',  label: 'Bokningar', icon: <IcoBokningar />, badge: newClientBookCount },
  ];
  const settingsNavItems = [
    { key: 'brand', label: 'Varumärke', icon: <IcoBrand /> },
  ];

  // När en kund är vald markerar vi ingen tab som aktiv i sidebaren
  const currentNav = selectedClient ? null : activeTab;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme.page_bg }}>

      {/* ── Sidebar ────────────────────────────────────────────────────────────── */}
      <div className={`fr-sidebar${sidebarOpen ? ' is-open' : ''}`} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        background: theme.sidebar_bg,
        borderRight: `1px solid ${theme.sidebar_border}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 200,
        overflowY: 'auto',
      }}>

        {/* Logotyp / varumärkesmarkör */}
        <div style={{ padding: '20px 16px 16px', borderBottom: `1px solid ${theme.sidebar_border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '28px', height: '28px',
              background: theme.sidebar_active_bg,
              borderRadius: '8px',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {brandLogoUrl
                ? <img src={brandLogoUrl} alt="" style={{ height: '18px', width: '18px', objectFit: 'contain', borderRadius: '4px' }} />
                : <div style={{ width: '10px', height: '10px', background: theme.accent, borderRadius: '3px' }} />
              }
            </div>
            <span style={{
              fontSize: '0.875em', fontWeight: '500',
              color: theme.sidebar_text_active, letterSpacing: '-0.2px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {brandDisplayName || therapist?.business_name || therapist?.name || 'Rehab'}
            </span>
          </div>
        </div>

        {/* Huvud-nav */}
        <nav style={{ flex: 1, padding: '10px 10px 0' }}>
          {mainNavItems.map(item => {
            const isActive = currentNav === item.key;
            return (
              <button
                key={item.key}
                onClick={() => { setSelectedClient(null); setBookFor(null); setActiveTab(item.key); setSidebarOpen(false); }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = theme.sidebar_active_bg;
                    e.currentTarget.style.color = theme.sidebar_text_active;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.sidebar_text;
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 10px',
                  marginBottom: '1px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  background: isActive ? theme.sidebar_active_bg : 'transparent',
                  color: isActive ? theme.sidebar_text_active : theme.sidebar_text,
                  fontSize: '0.875em',
                  fontWeight: '400',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                  transition: 'background 0.1s, color 0.1s',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                  <span style={{ color: isActive ? theme.accent : 'currentColor', display: 'flex', alignItems: 'center' }}>
                    {item.icon}
                  </span>
                  {item.label}
                </span>
                {item.badge > 0 && (
                  <span style={{
                    background: theme.accent, color: 'white',
                    borderRadius: '999px', padding: '1px 6px',
                    fontSize: '0.72em', fontWeight: '500',
                    minWidth: '18px', textAlign: 'center',
                  }}>
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}

          {/* Inställningar-sektion */}
          <div style={{ marginTop: '20px', marginBottom: '6px', padding: '0 10px' }}>
            <span style={{ fontSize: '0.67em', fontWeight: '400', color: theme.sidebar_text_muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Inställningar
            </span>
          </div>
          {settingsNavItems.map(item => {
            const isActive = !item.disabled && currentNav === item.key;
            return (
              <button
                key={item.key}
                onClick={item.disabled ? undefined : () => { setSelectedClient(null); setBookFor(null); setActiveTab(item.key); setSidebarOpen(false); }}
                onMouseEnter={e => {
                  if (!isActive && !item.disabled) {
                    e.currentTarget.style.background = theme.sidebar_active_bg;
                    e.currentTarget.style.color = theme.sidebar_text_active;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive && !item.disabled) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = item.disabled ? theme.sidebar_text_muted : theme.sidebar_text;
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  width: '100%',
                  padding: '7px 10px',
                  marginBottom: '1px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: item.disabled ? 'default' : 'pointer',
                  background: isActive ? theme.sidebar_active_bg : 'transparent',
                  color: item.disabled ? theme.sidebar_text_muted : isActive ? theme.sidebar_text_active : theme.sidebar_text,
                  fontSize: '0.875em',
                  fontWeight: '400',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                  opacity: item.disabled ? 0.6 : 1,
                  transition: 'background 0.1s, color 0.1s',
                }}
              >
                <span style={{ color: isActive ? theme.accent : 'currentColor', display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </span>
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Botten: user chip + logga ut */}
        <div style={{ padding: '12px 10px 16px', borderTop: `1px solid ${theme.sidebar_border}` }}>
          {isPlatformAdmin && (
            <button
              onClick={onNavigateToOwner}
              onMouseEnter={e => { e.currentTarget.style.background = theme.sidebar_active_bg; e.currentTarget.style.color = theme.sidebar_text_active; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.sidebar_text; }}
              style={{
                width: '100%',
                padding: '7px 12px',
                background: 'transparent',
                border: `1px solid ${theme.sidebar_border}`,
                color: theme.sidebar_text,
                borderRadius: '7px',
                cursor: 'pointer',
                fontSize: '0.78em',
                fontWeight: '500',
                textAlign: 'left',
                marginBottom: '10px',
                transition: 'background 0.1s, color 0.1s',
                boxSizing: 'border-box',
              }}
            >
              Klinikverktyget Admin ↗
            </button>
          )}
          <div style={{ background: theme.sidebar_active_bg, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
            <div style={{ fontSize: '0.78em', fontWeight: '500', color: theme.sidebar_text_active, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {therapist?.name || session?.user?.email?.split('@')[0] || '—'}
            </div>
            <div style={{ fontSize: '0.7em', color: theme.sidebar_text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {brandDisplayName || therapist?.business_name || session?.user?.email || '—'}
            </div>
          </div>
          <button
            onClick={() => supabase.auth.signOut()}
            onMouseEnter={e => { e.currentTarget.style.background = theme.sidebar_active_bg; e.currentTarget.style.color = theme.sidebar_text_active; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.sidebar_text; }}
            style={{
              width: '100%',
              padding: '7px 12px',
              background: 'transparent',
              border: `1px solid ${theme.sidebar_border}`,
              color: theme.sidebar_text,
              borderRadius: '7px',
              cursor: 'pointer',
              fontSize: '0.82em',
              textAlign: 'left',
              transition: 'background 0.1s, color 0.1s',
              boxSizing: 'border-box',
            }}
          >
            Logga ut
          </button>
        </div>
      </div>

      {/* ── Overlay-backdrop (stänger sidebar på mobil) ────────────────────────── */}
      {sidebarOpen && (
        <div className="fr-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Huvudinnehåll ───────────────────────────────────────────────────────── */}
      <div className="fr-main" style={{ flex: 1, marginLeft: SIDEBAR_WIDTH, minHeight: '100vh' }}>
        {/* Mobil: menyknapp i en egen rad överst (T-16 M1) – täcker inte innehållet */}
        <div className="fr-mobile-topbar">
          <button className="fr-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Öppna meny">
            <span /><span /><span />
          </button>
          <span className="fr-mobile-topbar-name">{brandDisplayName || 'Meny'}</span>
        </div>
        <div style={{ maxWidth: activeTab === 'bookings' ? '1180px' : '860px', margin: '0 auto', padding: '32px 24px' }}>

          {/* ── Kundprofil ── */}
          {selectedClient && (
            <ClientProfile
              therapist={therapist}
              client={selectedClient}
              onBack={() => { setSelectedClient(null); fetchData(); }}
              onNewBooking={() => { setBookFor(selectedClient); setSelectedClient(null); setActiveTab('bookings'); }}
              onDeleteClient={() => deleteClient(selectedClient.id)}
              accentColor={accentColor}
            />
          )}

          {/* ── Hem (T-12: egen vy i HomeView.js) ── */}
          {!selectedClient && activeTab === 'hem' && therapist && (
            <HomeView
              therapist={therapist}
              clients={clients}
              theme={theme}
              onOpenClient={(client) => setSelectedClient(client)}
              onOpenBookings={() => setActiveTab('bookings')}
              onAddClient={() => { setActiveTab('clients'); setShowAddClientForm(true); }}
              onNewBooking={() => setActiveTab('bookings')}
              onCountsChanged={() => fetchNotifCounts(therapist)}
            />
          )}

          {/* ── Kunder (T-12: egen vy i ClientsView.js) ── */}
          {!selectedClient && activeTab === 'clients' && therapist && (
            <ClientsView
              therapist={therapist}
              clients={clients}
              theme={theme}
              clientUnreadMsg={clientUnreadMsg}
              isNewClient={isNewClient}
              onOpenClient={(client) => setSelectedClient(client)}
              onMarkSeen={(id) => markClientAsSeen(id)}
              onAddClient={() => setShowAddClientForm(true)}
              addForm={showAddClientForm ? (
                <form onSubmit={submitNewClient} style={{ border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, padding: '18px 20px', background: theme.card_bg, boxShadow: theme.card_shadow }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '500', color: theme.text_primary, marginBottom: '4px' }}>Namn <span style={{ color: STATUS_COLORS.error }}>*</span></label>
                    <input
                      autoFocus
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      placeholder="För- och efternamn"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', boxSizing: 'border-box', outline: 'none', color: theme.text_primary }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '500', color: theme.text_primary, marginBottom: '4px' }}>E-post</label>
                      <input
                        type="email"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        placeholder="kund@exempel.se"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${duplicateEmailClient ? STATUS_COLORS.warning : theme.card_border}`, fontSize: '0.875em', boxSizing: 'border-box', outline: 'none', color: theme.text_primary }}
                      />
                      <p style={{ margin: '5px 0 0', fontSize: '0.75em', color: NEUTRAL.muted, lineHeight: 1.4 }}>Läs upp adressen för kunden innan du sparar – fel adress kan ge fel person tillgång.</p>
                      {duplicateEmailClient && (
                        <div role="alert" style={{ marginTop: '6px', background: STATUS_COLORS.warning_bg, border: `1px solid ${STATUS_COLORS.warning}`, borderRadius: '7px', padding: '7px 10px', fontSize: '0.78em', color: '#92400E', lineHeight: 1.4 }}>
                          Den här adressen finns redan på kunden <strong>{duplicateEmailClient.name}</strong>. Kontrollera att det inte är samma person innan du sparar.
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '500', color: theme.text_primary, marginBottom: '4px' }}>Telefon</label>
                      <input
                        type="tel"
                        value={newClientPhone}
                        onChange={(e) => setNewClientPhone(e.target.value)}
                        placeholder="070 123 45 67"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '0.875em', boxSizing: 'border-box', outline: 'none', color: theme.text_primary }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="submit"
                      disabled={!newClientName.trim() || addingClient}
                      style={{ background: newClientName.trim() ? theme.accent : theme.accent + '80', color: 'white', border: 'none', padding: '8px 18px', borderRadius: theme.btn_radius, cursor: newClientName.trim() ? 'pointer' : 'default', fontSize: '0.875em', fontWeight: '500' }}
                    >
                      {addingClient ? 'Sparar...' : 'Lägg till kund'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelAddClient}
                      style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, color: theme.text_secondary, padding: '8px 16px', borderRadius: theme.btn_radius, cursor: 'pointer', fontSize: '0.875em' }}
                    >
                      Avbryt
                    </button>
                  </div>
                </form>
              ) : null}
            />
          )}

          {/* ── Övningar ── */}
          {!selectedClient && activeTab === 'exercises' && therapist && (
            <Exercises therapist={therapist} accentColor={accentColor} />
          )}

          {/* ── Program ── */}
          {!selectedClient && activeTab === 'programs' && therapist && (
            <Programs
              therapist={therapist}
              accentColor={accentColor}
              onNavigateToClient={(client) => setSelectedClient(client)}
            />
          )}

          {/* ── Bokningar ── */}
          {!selectedClient && activeTab === 'bookings' && therapist && (
            <Bookings therapist={therapist} clients={clients} accentColor={accentColor} onOpenClient={setSelectedClient} bookFor={bookFor} onBookForDone={() => setBookFor(null)} />
          )}

          {/* ── Varumärke ── */}
          {!selectedClient && activeTab === 'brand' && therapist && (
            <BrandAdmin
              therapist={therapist}
              onBrandSaved={(brand) => {
                setAccentColor(brand.accentColor || KLINIO_DEFAULT_ACCENT);
                setBrandLogoUrl(brand.logoUrl ?? null);
                setBrandDisplayName(brand.displayName ?? null);
                setBrandTagline(brand.tagline ?? null);
              }}
            />
          )}

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
